import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import UserService from "services/UserService";
import { AuthHelper } from "utils/AuthHelper";
import {
  PLEASE_ENTER_CONFIRM_PASSWORD,
  PASSWORDS_DONT_MATCH,
  PLEASE_ENTER_PASSWORD_8_CHARACTERS,
  PASSWORD_CHANGED_SUCCESSFUL,
  PASSWORD_CHANGED_FAILED,
  PLEASE_TRY_AGAIN_LATER,
  PLEASE_ENTER_OLD_PASSWORD,
  NEW_PASSWORD_DIFFERENT_FROM_OLD_PASSWORD,
  PLEASE_ENTER_PASSWORD,
  LABEL_ENTER_OLD_PASSWORD,
  LABEL_ENTER_NEW_PASSWORD,
  LABEL_ENTER_CONFIRM_PASSWORD,
} from "configurations/MessageConstants";
import "styles/PasswordPopup.css";

const ChangePasswordPopup = ({ show, onClose }) => {
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    trigger,
  } = useForm({
    defaultValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    mode: "onChange",
  });

  const newPassword = watch("newPassword");
  const oldPassword = watch("oldPassword");
  const confirmPassword = watch("confirmPassword");

  useEffect(() => {
    if (confirmPassword) {
      trigger("confirmPassword");
    }
  }, [newPassword, trigger, confirmPassword]);

  const onSubmit = async (data) => {
    try {
      await UserService.changePassword(data.oldPassword, data.newPassword);
      toast.success(PASSWORD_CHANGED_SUCCESSFUL);
      handleClose();
    } catch (error) {
      console.error(PASSWORD_CHANGED_FAILED, error);
      toast.error(
        <div>
          {PASSWORD_CHANGED_FAILED}
          <br />
          {PLEASE_TRY_AGAIN_LATER}
        </div>
      );
    }
  };

  const handleClose = () => {
    reset();
    setShowOldPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      className="password-modal"
      centered
      aria-labelledby="change-password-popup"
    >
      <Modal.Header closeButton className="border-0">
        <Modal.Title id="change-password-popup">Change Password</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Form.Group className="mb-4">
            <Form.Label>Old Password</Form.Label>
            <div className="position-relative">
              <Controller
                name="oldPassword"
                control={control}
                rules={{
                  required: PLEASE_ENTER_OLD_PASSWORD,
                  validate: AuthHelper.isValidPassword,
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type={showOldPassword ? "text" : "password"}
                    placeholder={LABEL_ENTER_OLD_PASSWORD}
                    className="input-underline"
                    disabled={isSubmitting}
                    isInvalid={!!errors.oldPassword}
                    maxLength={50}
                  />
                )}
              />
              <Button
                variant="link"
                className="password-toggle-button"
                onClick={() => setShowOldPassword(!showOldPassword)}
                aria-label={showOldPassword ? "Hide password" : "Show password"}
              >
                <i
                  className={`fa fa-eye${showOldPassword ? "" : "-slash"}`}
                  aria-hidden="true"
                ></i>
              </Button>
              <Form.Control.Feedback type="invalid">
                {errors.oldPassword?.message}
              </Form.Control.Feedback>
            </div>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>New Password</Form.Label>
            <div className="position-relative">
              <Controller
                name="newPassword"
                control={control}
                rules={{
                  required: PLEASE_ENTER_PASSWORD,
                  validate: {
                    passwordValidation: AuthHelper.isValidPassword,
                    differentFromOld: (value) =>
                      value !== oldPassword ||
                      NEW_PASSWORD_DIFFERENT_FROM_OLD_PASSWORD,
                  },
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type={showNewPassword ? "text" : "password"}
                    placeholder={LABEL_ENTER_NEW_PASSWORD}
                    className="input-underline"
                    disabled={isSubmitting}
                    isInvalid={!!errors.newPassword}
                    autoComplete="new-password"
                    maxLength={50}
                  />
                )}
              />
              <Button
                variant="link"
                className="password-toggle-button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                aria-label={showNewPassword ? "Hide password" : "Show password"}
              >
                <i
                  className={`fa fa-eye${showNewPassword ? "" : "-slash"}`}
                  aria-hidden="true"
                ></i>
              </Button>
              <Form.Control.Feedback type="invalid">
                {errors.newPassword?.message}
              </Form.Control.Feedback>
            </div>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Confirm Password</Form.Label>
            <div className="position-relative">
              <Controller
                name="confirmPassword"
                control={control}
                rules={{
                  required: PLEASE_ENTER_CONFIRM_PASSWORD,
                  validate: {
                    notEmpty: (value) =>
                      value.trim() !== "" || PLEASE_ENTER_CONFIRM_PASSWORD,
                    matchesPassword: (value) =>
                      value === newPassword || PASSWORDS_DONT_MATCH,
                  },
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={LABEL_ENTER_CONFIRM_PASSWORD}
                    className="input-underline"
                    disabled={isSubmitting}
                    isInvalid={!!errors.confirmPassword}
                    autoComplete="new-password"
                    maxLength={50}
                  />
                )}
              />
              <Button
                variant="link"
                className="password-toggle-button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={
                  showConfirmPassword ? "Hide password" : "Show password"
                }
              >
                <i
                  className={`fa fa-eye${showConfirmPassword ? "" : "-slash"}`}
                  aria-hidden="true"
                ></i>
              </Button>
              <Form.Control.Feedback type="invalid">
                {errors.confirmPassword?.message}
              </Form.Control.Feedback>
            </div>
          </Form.Group>

          <div className="d-flex gap-2 justify-content-end">
            <Button
              variant="outline-secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  Changing
                  <Spinner animation="border" size="sm" className="ms-2" />
                </>
              ) : (
                "Change Password"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ChangePasswordPopup;
